import React from 'react'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import {
  marginSizes, modal,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Card from '@jarvis.oddle/core/components/Card'
import { H3, H4, P } from '@jarvis.oddle/core/components/Typo'
import Image from '@jarvis.oddle/core/components/Image'
import Button from '@jarvis.oddle/core/components/Button'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import CartProgressBars from 'src/components/molecules/CartProgressBar'
import ProductImageState from 'src/components/molecules/ProductImageState'
import { useAppContext } from 'src/containers/AppContext'
import modalController from 'src/decorators/ModalController'

const ToastContainer = styled(Card)`
  padding: 0 !important;
  transform: translateY(30px);
  width: 360px;
  overflow: hidden;
  display: none;
  ${breakpoint('desktop')`
    display: block;
  `}
`

const StyledProductImage = styled(Image).attrs(() => ({
  height: 80,
  width: 80,
  objectFit: 'cover'
}))`
  min-width: 80px;
  padding-right: ${padding[paddingSizes.small]};
`

const Item = ({
  name,
  quantity,
  price,
  imageUrl
}: {
  name: string
  quantity: number
  price: number
  imageUrl: string
}): JSX.Element => {
  const { getProductImagePath, formatMoney } = useAppContext()
  return (
    <Flex marginBottom={marginSizes.small}>
      <ProductImageState>
        {({ showImage, showDefaultImage }) => {
          const imageSrc = getProductImagePath(imageUrl, showDefaultImage, {
            shouldCache: true,
            containerWidth: 120,
            containerHeight: 120
          })
          return (
            showImage && imageSrc && <StyledProductImage src={imageSrc} />
          )
        }}
      </ProductImageState>
      <Flex
        flexDirection='column'
        justifyContent='space-between'
        flexGrow={1}
      >
        <Flex justifyContent='space-between'>
          <H4 bold margin={marginSizes.none}>
            {name}
          </H4>
          <Flex alignSelf='flex-end'>
            <P margin={marginSizes.none}>{`x${quantity}`}</P>
          </Flex>
        </Flex>
        <P margin={marginSizes.none}>{formatMoney(price)}</P>
      </Flex>
    </Flex>
  )
}

const AddToCartToast = ({
  items,
  closeToast
}: {
  items: {
    name: string
    quantity: number
    price: number
    imageUrl: string
  }[]
  closeToast?: (callback) => void
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)

  const goToCart = () => {
    closeToast(() => modalController.showModal('CartDrawer'))
  }

  return (
    <ToastContainer>
      <CartProgressBars />
      <Div
        paddingTop={paddingSizes.small}
        paddingBottom={paddingSizes.large}
        paddingLeft={paddingSizes.medium}
        paddingRight={paddingSizes.medium}
      >
        <H3 bold>{t('Item(s) Added')}</H3>
        {items.map(({ name, quantity, price, imageUrl }) => (
          <Item
            key={name}
            name={name}
            quantity={quantity}
            price={price}
            imageUrl={imageUrl}
          />
        ))}
        <Button width='100%' onClick={goToCart} secondary>
          {t('View Cart')}
        </Button>
      </Div>
    </ToastContainer>
  )
}

export default AddToCartToast
